import React, { useState } from 'react';

import useClaimFieldsConfiguration from '~/components/ClaimPage/hooks/useClaimFieldsConfiguration';
import { CLAIM_FIELD_CONFIG_TYPE } from '~/components/ClaimPage/types';
import { MainCard, Text } from '~/components/core';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';

import { useSysconfig } from '../../SystemConfigurationScreen';

import { moveDisplayedField, updateField } from './apiService';
import type { BarFieldKey } from './ClaimBarFieldList';
import ClaimBarFieldList, { useDisplayedClaimBarFields } from './ClaimBarFieldList';
import { BAR_TEXTS as TEXTS, MOVE_TEXTS } from './texts';

/** The Default's claim bar with the additional-fields flag off: every change is written as it is made. */
const BarFieldsConfiguration = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { fieldsConfiguration, reloadData, isError } = useClaimFieldsConfiguration(CLAIM_FIELD_CONFIG_TYPE.CLAIM_BAR);
  const displayedFields = useDisplayedClaimBarFields(fieldsConfiguration);
  const [isSaving, setIsSaving] = useState(false);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  // The dropped order, shown until the reload after the reorder brings the server's order (which also reverts a
  // failed reorder).
  const [droppedOrder, setDroppedOrder] = useState<typeof displayedFields | null>(null);
  const orderedFields = droppedOrder ?? displayedFields;

  const updateBarField = async (field_key: BarFieldKey, updates: { isEnabled?: boolean; displayName?: string }) => {
    setIsSaving(true);
    try {
      await updateField(organization?.id, CLAIM_FIELD_CONFIG_TYPE.CLAIM_BAR, field_key, updates);
      await reloadData();
    } finally {
      setIsSaving(false);
    }
  };

  const handleReorder = async ({ fromIndex, toIndex }: DndOrderedListReorder) => {
    setIsSaving(true);

    const reordered = [...orderedFields];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setDroppedOrder(reordered);

    try {
      const outcome = await moveDisplayedField(
        organization?.id,
        CLAIM_FIELD_CONFIG_TYPE.CLAIM_BAR,
        fieldsConfiguration,
        orderedFields,
        fromIndex,
        toIndex
      );
      if (outcome === 'listChanged') {
        showSnackbar(MOVE_TEXTS.listChanged, 'warning');
      }
    } finally {
      // Resync to the server's order (reverts on failure) and release the slot even if it fails.
      try {
        await reloadData();
      } finally {
        setDroppedOrder(null);
        setIsSaving(false);
      }
    }
  };

  return isError ? (
    <SkeletonTable rowsCount={12} columnsCount={1} isError={isError} />
  ) : (
    <MainCard type="elevated" title={TEXTS.sectionTitle}>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="-mt-4">
        {TEXTS.sectionDescription}
      </Text>
      <ClaimBarFieldList
        displayedFields={orderedFields}
        onToggleEnabled={(fieldKey, isEnabled) => updateBarField(fieldKey, { isEnabled })}
        onDisplayNameChange={(fieldKey, displayName) => updateBarField(fieldKey, { displayName })}
        onReorder={handleReorder}
        disabled={isSaving}
      />
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </MainCard>
  );
};

export default BarFieldsConfiguration;
